/**
 * Reports Chatbot Component
 *
 * AI-powered assistant for analyzing reports data.
 * Uses two-phase LLM processing:
 * 1. Determines which data sources to query based on user question
 * 2. Fetches and summarizes data, then generates response
 *
 * This component is reusable - can be used for Reports AI, Subject Management AI, etc.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Dropdown } from 'antd';
import ChatMessageList from '../reports-ai-core/ChatMessageList';
import ChatInputArea from '../reports-ai-core/ChatInputArea';
import { useChatSession } from '../reports-ai-core/useChatSession';
import ApiReportsChatbot from '../../../../api/reports-chatbot';
import ApiSubjectManagementAi from '../../../../api/subjectManagementAi';
import ApiSubjectsManagement from '../../../../api/subjects-manager';
import { useReportsProgressTracking } from '../reports-ai-core/useReportsProgressTracking';
import './reports-chatbot.css';

const ReportsChatbot = ({
  userId,
  currentRoute,
  activeFilters = {},
  visibleDataSummary = null,
  projectId = null,
  siteIds = null,
  onClose,
  // New configurable props
  apiClient = ApiReportsChatbot,
  progressTrackingHook = useReportsProgressTracking,
  title = 'AI Assistant',
  welcomeMessage = null,
  localStoragePrefix = 'reportsChatbot',
  // Dynamic API clients for context switching
  reportsApiClient = ApiReportsChatbot,
  subjectApiClient = ApiSubjectManagementAi,
}) => {
  const navigate = useNavigate();
  const { projectCode } = useParams();
  const [inputMessage, setInputMessage] = useState('');
  const [recentSubjects, setRecentSubjects] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem(`${localStoragePrefix}_collapsed`);
    return stored ? JSON.parse(stored) : false;
  });
  const [position, setPosition] = useState(() => {
    const stored = localStorage.getItem(`${localStoragePrefix}_position`);
    const parsed = stored ? JSON.parse(stored) : null;
    if (parsed) {
      const isValid = parsed.x >= 0 &&
                      parsed.y >= 0 &&
                      parsed.x < window.innerWidth - 100 &&
                      parsed.y < window.innerHeight - 100;
      if (!isValid) {
        localStorage.removeItem(`${localStoragePrefix}_position`);
        return null;
      }
    }
    return parsed;
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [size, setSize] = useState(() => {
    const stored = localStorage.getItem(`${localStoragePrefix}_size`);
    return stored ? JSON.parse(stored) : { width: 600, height: 650 };
  });
  const [isExporting, setIsExporting] = useState(false);

  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const chatbotRef = useRef(null);

  // Build report context
  const reportContext = {
    currentRoute,
    activeFilters,
    visibleDataSummary,
    projectId,
    siteIds
  };

  // Determine initial context based on current route and available data
  const initialContext = useMemo(() => {
    // If on a specific subject detail page, use subject-specific context
    if (currentRoute && currentRoute.includes('/subject/') && visibleDataSummary?.subjectId) {
      return {
        type: 'subject',
        identifier: visibleDataSummary.email || visibleDataSummary.displayId
      };
    }

    // If on subject management pages, use general subject context
    if (currentRoute && currentRoute.includes('subject')) {
      return { type: 'subject' };
    }

    // Default to reports context
    return { type: 'reports' };
  }, [currentRoute, visibleDataSummary]);

  // Callback when title is auto-generated
  const handleTitleUpdated = (updatedSessionId, newTitle) => {
    // No need to refresh anything in modal
  };

  // Use chat session hook
  const {
    sessionId,
    messages,
    loading,
    error,
    setError,
    stageName,
    elapsedSeconds,
    initializeSession,
    createNewSession,
    sendMessage,
    cancelRequest,
    deleteSession
  } = useChatSession(
    userId,
    reportContext,
    handleTitleUpdated,
    apiClient, // Default API for session management
    progressTrackingHook,
    welcomeMessage,
    null, // sessionSource
    reportsApiClient, // reportsApiClient for dynamic routing
    subjectApiClient // subjectApiClient for dynamic routing
  );

  // Initialize session on mount
  // Use ref to prevent double initialization from React StrictMode
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      initializeSession();
    }
  }, [initializeSession]);

  // Save collapsed state
  useEffect(() => {
    localStorage.setItem(`${localStoragePrefix}_collapsed`, JSON.stringify(isCollapsed));
  }, [isCollapsed, localStoragePrefix]);

  // Save position
  useEffect(() => {
    if (position) {
      localStorage.setItem(`${localStoragePrefix}_position`, JSON.stringify(position));
    }
  }, [position, localStoragePrefix]);

  // Save size
  useEffect(() => {
    localStorage.setItem(`${localStoragePrefix}_size`, JSON.stringify(size));
  }, [size, localStoragePrefix]);

  // Fetch recent subjects for autocomplete
  useEffect(() => {
    const fetchRecentSubjects = async () => {
      try {
        const response = await ApiSubjectsManagement.getSubjects({
          limit: 20,
          sortBy: 'updatedAt',
          sortOrder: 'desc'
        });
        // Handle both response.data.subjects and response.data.data.subjects
        const subjects = response.data?.data?.subjects || response.data?.subjects || [];
        setRecentSubjects(subjects);
      } catch (err) {
        console.error('[ReportsChatbot] Failed to fetch recent subjects:', err);
        // Don't break the UI if subjects fetch fails
        setRecentSubjects([]);
      }
    };

    fetchRecentSubjects();
  }, []);

  const handleSendMessage = (messageOverride) => {
    const messageToSend = messageOverride !== undefined ? messageOverride : inputMessage;
    if (!messageToSend.trim() || loading) return;
    sendMessage(messageToSend);
    setInputMessage('');
  };

  // Drag handlers
  const handleMouseDown = (e) => {
    if (e.target.closest('.reports-chatbot-header-actions') ||
        e.target.closest('.reports-chatbot-resize-handle')) {
      return;
    }

    const rect = chatbotRef.current?.getBoundingClientRect();
    if (!rect) return;

    setIsDragging(true);
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const newX = Math.max(0, Math.min(window.innerWidth - 100, e.clientX - dragOffset.x));
        const newY = Math.max(0, Math.min(window.innerHeight - 50, e.clientY - dragOffset.y));
        setPosition({ x: newX, y: newY });
      }

      if (isResizing) {
        const deltaX = e.clientX - resizeStartRef.current.x;
        const deltaY = e.clientY - resizeStartRef.current.y;
        setSize({
          width: Math.max(350, Math.min(800, resizeStartRef.current.width + deltaX)),
          height: Math.max(400, Math.min(900, resizeStartRef.current.height + deltaY))
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragOffset]);

  const handleResizeStart = (e) => {
    e.stopPropagation();
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    };
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleExpandToFullScreen = () => {
    if (sessionId && projectCode) {
      navigate(`/${projectCode}/analytics/ai-assistant/${sessionId}`);
    } else if (projectCode) {
      navigate(`/${projectCode}/analytics/ai-assistant`);
    }
  };

  const handleExportSession = async () => {
    if (!sessionId || !userId) return;

    setIsExporting(true);
    try {
      const pdfBlob = await apiClient.exportSession(sessionId, userId);

      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
      const filename = `Reports_AI_Session_${timestamp}.pdf`;
      link.download = filename;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export session:', err);
      setError('Failed to export session. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const chatbotStyle = {
    ...(position && {
      left: `${position.x}px`,
      top: `${position.y}px`,
      right: 'auto',
      bottom: 'auto'
    }),
    width: `${size.width}px`,
    height: isCollapsed ? 'auto' : `${size.height}px`
  };

  // Build menu items based on session state
  const menuItems = [];
  
  // Add session-specific actions if session exists
  if (sessionId) {
    menuItems.push({
      key: 'new-session',
      label: 'New session',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
      </svg>,
      onClick: ({ domEvent }) => {
        domEvent?.stopPropagation();
        createNewSession();
      }
    });
    
    menuItems.push({
      key: 'export',
      label: 'Export as PDF',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M12,19L8,15H10.5V12H13.5V15H16L12,19Z"/>
      </svg>,
      disabled: isExporting,
      onClick: ({ domEvent }) => {
        domEvent?.stopPropagation();
        handleExportSession();
      }
    });
    
    menuItems.push({
      key: 'delete',
      label: 'Delete session',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z"/>
      </svg>,
      danger: true,
      onClick: ({ domEvent }) => {
        domEvent?.stopPropagation();
        deleteSession();
      }
    });
    
    menuItems.push({ type: 'divider', key: 'divider-1' });
  }
  
  // Always add expand option
  menuItems.push({
    key: 'expand',
    label: 'Expand to full-screen',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M7,14H5v5h5v-2H7V14M5,10h2V7h3V5H5V10M17,17h-3v2h5v-5h-2V17M14,5v2h3v3h2V5H14Z"/>
    </svg>,
    onClick: ({ domEvent }) => {
      domEvent?.stopPropagation();
      handleExpandToFullScreen();
    }
  });

  return (
    <div
      ref={chatbotRef}
      className={`reports-chatbot ${isCollapsed ? 'collapsed' : ''} ${isDragging ? 'dragging' : ''}`}
      style={chatbotStyle}
    >
      {/* Header */}
      <div
        className="reports-chatbot-header"
        onMouseDown={handleMouseDown}
      >
        <div className="reports-chatbot-header-title">
          {title}
          <button
            className="reports-chatbot-header-title-dropdown"
            onClick={(e) => {
              e.stopPropagation();
              toggleCollapse();
            }}
            title={isCollapsed ? 'Expand' : 'Collapse'}
            aria-label={isCollapsed ? 'Expand' : 'Collapse'}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 16 16" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              style={{ 
                transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}
            >
              <path d="M2.21973 7.03027C1.92683 6.73738 1.92683 6.26262 2.21973 5.96973C2.51262 5.67683 2.98738 5.67683 3.28027 5.96973L7.75 10.4395L12.2197 5.96973C12.5126 5.67683 12.9874 5.67683 13.2803 5.96973C13.5732 6.26262 13.5732 6.73738 13.2803 7.03027L8.28027 12.0303C7.98738 12.3232 7.51262 12.3232 7.21973 12.0303L2.21973 7.03027Z" fill="#B2B2C7"/>
            </svg>
          </button>
        </div>
        <div className="reports-chatbot-header-actions">
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <button
              className="reports-chatbot-icon-button"
              title="More options"
              aria-label="More options"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12,16A2,2 0 0,1 14,18A2,2 0 0,1 12,20A2,2 0 0,1 10,18A2,2 0 0,1 12,16M12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12A2,2 0 0,1 12,10M12,4A2,2 0 0,1 14,6A2,2 0 0,1 12,8A2,2 0 0,1 10,6A2,2 0 0,1 12,4Z"/>
              </svg>
            </button>
          </Dropdown>
          <button
            className="reports-chatbot-icon-button"
            onClick={toggleCollapse}
            title={isCollapsed ? 'Expand' : 'Minimize'}
            aria-label={isCollapsed ? 'Expand' : 'Minimize'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,13H5V11H19V13Z"/>
            </svg>
          </button>
          <button
            className="reports-chatbot-icon-button"
            onClick={onClose}
            title="Close"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41z"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Body */}
      {!isCollapsed && (
        <>
          {/* Messages */}
          <ChatMessageList
            messages={messages}
            loading={loading}
            stageName={stageName}
            elapsedSeconds={elapsedSeconds}
            onCancelRequest={cancelRequest}
          />

          {/* Error banner */}
          {error && (
            <div className="reports-chatbot-error">
              {error}
            </div>
          )}

          {/* Input area */}
          <ChatInputArea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onSend={handleSendMessage}
            disabled={loading}
            placeholder="Ask about your reports data..."
            recentSubjects={recentSubjects}
            initialContext={initialContext}
          />

          {/* Resize handle */}
          <div
            className="reports-chatbot-resize-handle"
            onMouseDown={handleResizeStart}
          />
        </>
      )}
    </div>
  );
};

export default ReportsChatbot;
